/**
 * Message Formatter Utility
 * Handles formatting of special message types like squad announcements
 */

/**
 * Detects if a message is a squad announcement
 * @param {string} message - The message text
 * @returns {boolean} - True if message is a squad announcement
 */
export function isSquadMessage(message) {
  const squadPattern = /^Squad:\s*(.+)$/i;
  return squadPattern.test(message.trim());
}

/**
 * Parses player names from squad message
 * @param {string} message - The squad message
 * @returns {Array} - Array of player names
 */
export function parseSquadPlayers(message) {
  const squadPattern = /^Squad:\s*(.+)$/i;
  const match = message.match(squadPattern);

  if (!match) return [];

  const playersText = match[1];

  // Split by common delimiters and clean up names
  let players = playersText
    .split(/[,\n]/) // Split by comma or newline first
    .map((player) => player.trim())
    .filter((player) => player.length > 0);

  // If we only have one item, try splitting by spaces (looking for multiple names)
  if (players.length === 1) {
    const spaceSplit = playersText.split(/\s+/);

    // Try to group names intelligently (assuming first and last names)
    if (spaceSplit.length > 2) {
      const groupedPlayers = [];
      let currentName = "";

      for (let i = 0; i < spaceSplit.length; i++) {
        const word = spaceSplit[i];

        // If this word starts with capital letter and we have a current name, it might be a new player
        if (word.match(/^[A-Z]/) && currentName && i > 0) {
          // Check if previous word also starts with capital (likely last name)
          const prevWord = spaceSplit[i - 1];
          if (prevWord && prevWord.match(/^[A-Z]/)) {
            groupedPlayers.push(currentName.trim());
            currentName = word;
          } else {
            currentName += " " + word;
          }
        } else {
          currentName += (currentName ? " " : "") + word;
        }
      }

      // Add the last name
      if (currentName) {
        groupedPlayers.push(currentName.trim());
      }

      // Only use grouped players if we got a reasonable number
      if (
        groupedPlayers.length > 1 &&
        groupedPlayers.length < spaceSplit.length
      ) {
        players = groupedPlayers;
      }
    }
  }

  return players.filter((player) => player.length > 0);
}

/**
 * Formats squad message into HTML
 * @param {string} message - The original message
 * @param {string} style - The formatting style ('grid', 'badges', 'list')
 * @returns {string} - Formatted HTML
 */
export function formatSquadMessage(message, style = "badges") {
  const players = parseSquadPlayers(message);

  if (players.length === 0) {
    return message; // Return original if parsing fails
  }

  switch (style) {
    case "grid":
      return formatSquadGrid(players);
    case "list":
      return formatSquadList(players);
    case "badges":
    default:
      return formatSquadBadges(players);
  }
}

/**
 * Format squad as badges (default style)
 */
function formatSquadBadges(players) {
  const playerBadges = players
    .map(
      (player) =>
        `<span class="squad-player-badge">${escapeHtml(player)}</span>`,
    )
    .join(" ");

  return `
    <div class="squad-announcement">
      <div class="squad-header">
        <span class="squad-title">Squad</span>
        <span class="squad-count">(${players.length} players)</span>
      </div>
      <div class="squad-players-badges">
        ${playerBadges}
      </div>
    </div>
  `;
}

/**
 * Format squad as numbered list
 */
function formatSquadList(players) {
  const playerList = players
    .map(
      (player, index) =>
        `<li class="squad-player-item">${index + 1}. ${escapeHtml(player)}</li>`,
    )
    .join("");

  return `
    <div class="squad-announcement">
      <div class="squad-header">
        <span class="squad-title">Squad</span>
        <span class="squad-count">(${players.length} players)</span>
      </div>
      <ol class="squad-players-list">
        ${playerList}
      </ol>
    </div>
  `;
}

/**
 * Format squad as grid layout
 */
function formatSquadGrid(players) {
  const playersPerRow = 3;
  const rows = [];

  for (let i = 0; i < players.length; i += playersPerRow) {
    const rowPlayers = players.slice(i, i + playersPerRow);
    const cells = rowPlayers
      .map(
        (player) => `<div class="squad-grid-cell">${escapeHtml(player)}</div>`,
      )
      .join("");

    // Fill remaining cells in row
    const emptyCells = playersPerRow - rowPlayers.length;
    const emptyCellsHtml = Array(emptyCells)
      .fill('<div class="squad-grid-cell squad-grid-empty"></div>')
      .join("");

    rows.push(`<div class="squad-grid-row">${cells}${emptyCellsHtml}</div>`);
  }

  return `
    <div class="squad-announcement">
      <div class="squad-header">
        <span class="squad-title">Squad</span>
        <span class="squad-count">(${players.length} players)</span>
      </div>
      <div class="squad-players-grid">
        ${rows.join("")}
      </div>
    </div>
  `;
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text) {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Main function to process any message and apply formatting
 * @param {string} message - The original message
 * @param {Object} options - Formatting options
 * @returns {string} - Processed message (HTML or original text)
 */
export function processMessage(message, options = {}) {
  if (isSquadMessage(message)) {
    return formatSquadMessage(message, options.squadStyle || "badges");
  }

  // Add other message type processors here in the future
  // e.g., formation messages, injury reports, etc.

  return message; // Return original message if no special formatting needed
}
